// The large view of one crop of a recording or a photo, opened in place in its rail row: the
// picture whole (contained, never cut), a person's face docked in its corner when the pass has
// one, what was seen, and every fact the analysis knows about it. Shared by the people, faces,
// plates and events of the detail rail. Pure: the pictures and the facts arrive as props.
import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { ImageFallback } from '@/components/humint/Thumbnail'
import { useAuthedImageUrl } from '@/hooks/use-authed-image-url'
import { cn } from '@/utils/cn'

export interface CropFact {
  label: string
  value: ReactNode
}

/** The well a crop sits in, by what it shows: a standing figure, a face, a whole frame, a plate. */
const SHAPES = {
  figure: 'aspect-[3/4] max-h-96',
  face: 'aspect-square max-h-72',
  frame: 'aspect-video',
  plate: 'aspect-[4/1]',
} as const

export interface CropDetailProps {
  image: { url: string | null; alt: string; Icon: LucideIcon }
  /** The face of the person on a figure, docked in the picture's corner. */
  inset?: { url: string | null; alt: string } | null
  shape: keyof typeof SHAPES
  facts: readonly CropFact[]
  /** What was seen of it (a description, a reason), between the picture and the facts. */
  children?: ReactNode
}

function Picture({
  url,
  alt,
  Icon,
  className,
  fit,
}: {
  url: string | null
  alt: string
  Icon: LucideIcon
  className: string
  fit: 'contain' | 'cover'
}) {
  const src = useAuthedImageUrl(url)
  return (
    <span className={cn('relative block overflow-hidden bg-muted', className)}>
      <ImageFallback Icon={Icon} />
      {src ? (
        <img
          src={src}
          alt={alt}
          decoding="async"
          className={cn(
            'relative size-full',
            fit === 'contain' ? 'object-contain' : 'object-cover'
          )}
        />
      ) : (
        <span className="sr-only">{alt}</span>
      )}
    </span>
  )
}

export function CropDetail({ image, inset, shape, facts, children }: CropDetailProps) {
  return (
    <div
      className="flex flex-col gap-3 motion-safe:animate-in motion-safe:fade-in-0 motion-safe:zoom-in-[0.98] motion-safe:duration-200"
      data-testid="crop-detail"
    >
      <div className="relative">
        <Picture
          url={image.url}
          alt={image.alt}
          Icon={image.Icon}
          fit="contain"
          className={cn('mx-auto w-full rounded-md', SHAPES[shape])}
        />
        {inset ? (
          <Picture
            url={inset.url}
            alt={inset.alt}
            Icon={image.Icon}
            fit="cover"
            className="absolute right-2 bottom-2 size-20 rounded-md shadow-md ring-2 ring-card"
          />
        ) : null}
      </div>
      {children}
      {facts.length > 0 ? (
        <dl className="grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-xs">
          {facts.map(fact => (
            <div key={fact.label} className="contents">
              <dt className="text-muted-foreground">{fact.label}</dt>
              <dd className="min-w-0 break-words text-foreground tabular-nums">{fact.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </div>
  )
}
